import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {redirect, type LoaderFunction, type RouteObject} from '@github-ui/react-router'
import type {ComponentType} from 'react'

import {buildIssueNewChooseRoute} from './issue-new-choose-route'
import {buildIssueViewerRoute} from './issue-viewer-route'
import {buildIssuesLayoutRoute} from './issues-layout-route'
import {buildIssueNewRoute} from './issue-new-route'
import {buildIssuesIndexContentRoute, buildIssuesLabelContentRoute} from './issues-index-content-route'
import {buildLabelsRoute} from './labels-route'
import {labelsLegacyRedirectLoader} from './labels-legacy-redirect-loader'
import {buildMilestoneEditRoute} from './milestone-edit-route'
import {buildMilestoneNewRoute} from './milestone-new-route'
import {buildMilestoneIndexRoute} from './milestone-index-route'
import {buildSavedViewNewRoute, buildSavedViewRoute, buildSavedViewsRoute} from './saved-views-routes'

export interface IssuesRouteComponents {
  IssueNewChoose: ComponentType
  IssueViewer: ComponentType
  IssuesIndexContent: ComponentType
  IssuesLayout: ComponentType
  IssueNew: ComponentType
  Labels: ComponentType
  MilestoneEdit: ComponentType
  MilestoneNew: ComponentType
  MilestoneIndex: ComponentType
  RepositoryProjectsPage: ComponentType
  SavedView: ComponentType
  SavedViewNew: ComponentType
  SavedViews: ComponentType
}

// The loader always redirects, so nothing is ever shown here. But React Router still
// renders the matched route and warns if a leaf route has no Component. this
// fails the tests (vitest-fail-on-console), so we pass an empty component
const LabelsRedirectFallback = () => null

const legacySavedViewsLoader: LoaderFunction = ({request}) => {
  const response = redirect(request.url)
  response.headers.set('X-Remix-Reload-Document', 'true')
  return response
}

// React Router requires a child match for URLs below /issues. These no-op routes provide that match
// while IssuesIndexContent renders the page
// Inspired by packages/pull-requests/routes/create-pull-requests-routes.tsx
function IssuesIndexCleanPathRoute() {
  return null
}

const issuesIndexCleanPathRoutes: RouteObject[] = [
  {path: '/:owner/:repo/issues', index: true, Component: IssuesIndexCleanPathRoute},
  {path: '/:owner/:repo/issues/mentioned/:mentioned', Component: IssuesIndexCleanPathRoute},
  {path: '/:owner/:repo/issues/assigned/:assignee', Component: IssuesIndexCleanPathRoute},
  {path: '/:owner/:repo/issues/created_by/:author', Component: IssuesIndexCleanPathRoute},
  {path: '/:owner/:repo/issues/created_by/app/:author', Component: IssuesIndexCleanPathRoute},
]

function buildRepositoryProjectsRoutes(
  appBuilder: DataRouterApplicationBuilder<string>,
  Component: ComponentType,
): RouteObject[] {
  return [
    appBuilder
      .createQueryRouteConfig('repositoryProjectsRoute', {
        path: '/:owner/:repo/projects',
        queries: [],
      })
      .toRoute({Component}),
    appBuilder
      .createQueryRouteConfig('repositoryProjectTemplatesRoute', {
        path: '/:owner/:repo/projects/templates',
        queries: [],
      })
      .toRoute({Component}),
  ]
}

/**
 * Builds the Issues route objects for a given app builder.
 *
 * Route configs are bound to the app builder that creates them (embedded-data
 * hydration + query-key app name), so registering these routes under a different
 * app (e.g. `repo`) requires rebuilding them with that app's builder rather than
 * importing the `issues`-bound singletons directly.
 */
export function createIssuesRoutes(
  appBuilder: DataRouterApplicationBuilder<string>,
  components: IssuesRouteComponents,
): RouteObject[] {
  const {
    IssueNewChoose,
    IssueViewer,
    IssuesIndexContent,
    IssuesLayout,
    IssueNew,
    Labels,
    MilestoneEdit,
    MilestoneNew,
    MilestoneIndex,
    RepositoryProjectsPage,
    SavedView,
    SavedViewNew,
    SavedViews,
  } = components
  const issuesIndexDataRouterEnabled = isFeatureEnabled('issues_index_data_router')
  const issueViewerPavedPathEnabled = isFeatureEnabled('issue_viewer_paved_path')

  return [
    buildIssuesLayoutRoute(appBuilder).toRoute({
      Component: IssuesLayout,
      children: [
        buildLabelsRoute(appBuilder).toRoute({Component: Labels}),
        // Repo Issues#index only when issues_index_data_router is on. With the flag off,
        // this app does not match the path, so links hard-navigate to the Relay page.
        ...(issuesIndexDataRouterEnabled
          ? [
              buildIssuesIndexContentRoute(appBuilder).toRoute({
                Component: IssuesIndexContent,
                children: issuesIndexCleanPathRoutes,
              }),
              buildIssuesLabelContentRoute(appBuilder).toRoute({Component: IssuesIndexContent}),
              buildSavedViewsRoute(appBuilder).toRoute({Component: SavedViews}),
              buildSavedViewNewRoute(appBuilder).toRoute({Component: SavedViewNew}),
              buildSavedViewRoute(appBuilder).toRoute({Component: SavedView}),
            ]
          : []),
        buildIssueNewRoute(appBuilder).toRoute({Component: IssueNew}),
        buildIssueNewChooseRoute(appBuilder).toRoute({Component: IssueNewChoose}),
        ...(!issuesIndexDataRouterEnabled && issueViewerPavedPathEnabled
          ? [{path: '/:owner/:repo/issues/views', loader: legacySavedViewsLoader}]
          : []),
        // `issues#show` serves the Relay app unless the same flag is on, so registering this with
        // the flag off would shadow the canonical issue URL with a route that cannot load.
        ...(issueViewerPavedPathEnabled ? [buildIssueViewerRoute(appBuilder).toRoute({Component: IssueViewer})] : []),
        buildMilestoneNewRoute(appBuilder).toRoute({Component: MilestoneNew}),
        buildMilestoneEditRoute(appBuilder).toRoute({Component: MilestoneEdit}),
        buildMilestoneIndexRoute(appBuilder).toRoute({Component: MilestoneIndex}),
        ...buildRepositoryProjectsRoutes(appBuilder, RepositoryProjectsPage),
        // defining it as a plain object because its just a legacy-redirect path
        {path: '/:owner/:repo/issues/labels', loader: labelsLegacyRedirectLoader, Component: LabelsRedirectFallback},
      ],
    }),
  ]
}
